import { Suspense, useMemo, useRef, useState } from 'react'
import { Outlet } from 'react-router'
import {
  OnboardingFrameContext,
  type OnboardingFrameHandlers,
  type OnboardingFrameView,
} from '@/components/onboarding/wizard/frame'
import { MobileProgress } from '@/components/onboarding/wizard/mobile-progress'
import { StepRail } from '@/components/onboarding/wizard/step-rail'
import { OnboardingTopBar } from '@/components/onboarding/wizard/top-bar'
import { Skeleton } from '@/components/ui/skeleton'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { cn } from '@/lib/utils'

/**
 * The onboarding wizard's own full page (the design's `#scr-ob`): the sticky top
 * bar, then the step rail beside one focused card. The page inside fills the
 * rail, the trial chip and "Save and exit" with `useOnboardingFrame`; until it
 * names a step there is no rail and the page gets one centred column.
 */
export function OnboardingLayout() {
  useDocumentTitle('Set up a new workspace')
  const [view, setView] = useState<OnboardingFrameView | null>(null)
  const handlers = useRef<OnboardingFrameHandlers>({})
  const api = useMemo(
    () => ({
      setView,
      setHandlers: (next: OnboardingFrameHandlers) => {
        handlers.current = next
      },
    }),
    [],
  )
  const step = view?.step

  return (
    <OnboardingFrameContext.Provider value={api}>
      <div className="ob-root">
        <OnboardingTopBar
          trial={view?.trial}
          saving={view?.saving}
          onSaveExit={view?.canSaveExit ? () => handlers.current.onSaveExit?.() : undefined}
        />
        <div className={cn('ob', view?.wide && 'ob-widecol', step === undefined && 'ob-solo')}>
          {step !== undefined ? (
            <StepRail
              current={step}
              done={view?.done}
              todo={view?.todo}
              onJump={view?.canJump ? (n) => handlers.current.onJump?.(n) : undefined}
              help={
                <>
                  <b>Need a hand?</b>
                  <span>
                    Email <a href="mailto:support@ringout.ai">support@ringout.ai</a> — we reply within one business day.
                  </span>
                </>
              }
            />
          ) : null}
          <main className="ob-col">
            {step !== undefined ? <MobileProgress current={step} /> : null}
            <Suspense fallback={<Skeleton className="h-[480px] w-full max-w-[580px] rounded-[16px]" />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    </OnboardingFrameContext.Provider>
  )
}
